import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, DENOMS, formatDateTime, toNum } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { MoneyInput } from "@/components/MoneyInput";
import { toast } from "sonner";
import { Coins, Plus, Minus, Save, Calculator, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export default function Cash() {
  const [cash, setCash] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [openCalc, setOpenCalc] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/cash").then((r) => {
      setCash(r.data);
      setDraft({ ...r.data.denominations });
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const changeQty = (d, delta) => setDraft((s) => ({ ...s, [d]: Math.max(0, (Number(s[d]) || 0) + delta) }));
  const setQty = (d, v) => setDraft((s) => ({ ...s, [d]: Math.max(0, Number(v) || 0) }));

  const total = DENOMS.reduce((sum, d) => sum + d * (Number(draft[String(d)]) || 0), 0);

  const save = async () => {
    await api.post("/cash/update", { kind: "set", denominations: draft, notes: "Update manual" });
    toast.success("Kas diperbarui ✓"); load();
  };

  if (loading || !cash) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Stok Uang Fisik</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Kas Laci</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-change-calc" variant="outline" onClick={() => setOpenCalc(true)} className="gap-2">
            <Calculator className="h-4 w-4" /> Kalkulator Kembalian
          </Button>
          <Button data-testid="btn-cash-history" variant="outline" onClick={() => setOpenHistory(true)} className="gap-2">
            <History className="h-4 w-4" /> Riwayat
          </Button>
        </div>
      </div>

      <Card className="p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900/40 border-emerald-200 dark:border-emerald-800">
        <p className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold">Total Kas Fisik</p>
        <p className="mt-2 money text-4xl font-bold text-emerald-800 dark:text-emerald-300 tracking-tight">{rupiah(total)}</p>
        <p className="mt-1 text-xs text-muted-foreground">Terakhir diperbarui: {formatDateTime(cash.updated_at)}</p>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
        {DENOMS.map((d) => {
          const key = String(d);
          const qty = Number(draft[key]) || 0;
          const subtotal = d * qty;
          return (
            <Card key={d} data-testid={`denom-row-${d}`} className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="money text-lg font-bold tracking-tight">{rupiah(d)}</p>
                  <p className="text-xs text-muted-foreground">Subtotal: <span className="money font-medium text-foreground">{rupiah(subtotal)}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid={`denom-minus-${d}`} size="icon" variant="outline" className="h-9 w-9" onClick={() => changeQty(key, -1)}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    data-testid={`denom-qty-${d}`}
                    type="number"
                    className="w-16 text-center money font-semibold"
                    value={qty}
                    onChange={(e) => setQty(key, e.target.value)}
                  />
                  <Button data-testid={`denom-plus-${d}`} size="icon" variant="outline" className="h-9 w-9" onClick={() => changeQty(key, 1)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="sticky bottom-16 lg:bottom-0 pt-3 pb-2 bg-background z-10 -mx-4 lg:mx-0 px-4 lg:px-0 border-t lg:border-0">
        <Button data-testid="btn-save-cash" size="lg" className="w-full h-12 gap-2" onClick={save}>
          <Save className="h-4 w-4" /> Simpan Kas ({rupiah(total)})
        </Button>
      </div>

      <ChangeCalcDialog open={openCalc} onClose={() => setOpenCalc(false)} />
      <CashHistoryDialog open={openHistory} onClose={() => setOpenHistory(false)} />
    </div>
  );
}

function ChangeCalcDialog({ open, onClose }) {
  const [bill, setBill] = useState("");
  const [received, setReceived] = useState("");
  const [result, setResult] = useState(null);

  const calc = async () => {
    const r = await api.post("/cash/change-suggestion", { total_bill: toNum(bill), cash_received: toNum(received) });
    setResult(r.data);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Kalkulator Kembalian</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Total Bayar</Label><MoneyInput data-testid="calc-bill" value={bill} onChange={setBill} /></div>
            <div><Label>Uang Diterima</Label><MoneyInput data-testid="calc-received" value={received} onChange={setReceived} /></div>
          </div>
          <Button data-testid="btn-calculate" onClick={calc} className="w-full">Hitung</Button>
          {result && (
            <Card className="p-4 bg-accent/40">
              {result.error ? (
                <p className="text-sm text-rose-600">{result.error}</p>
              ) : (
                <>
                  <p className="text-xs uppercase text-muted-foreground font-semibold">Kembalian</p>
                  <p className="money text-3xl font-bold tracking-tight">{rupiah(result.change)}</p>
                  {Object.keys(result.denominations || {}).length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {Object.entries(result.denominations).map(([d, q]) => (
                        <div key={d} className="flex justify-between text-sm">
                          <span className="money">{rupiah(Number(d))}</span>
                          <span className="money font-semibold">× {q}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {result.unreachable > 0 && (
                    <p className="mt-3 text-xs text-rose-600">Sisa {rupiah(result.unreachable)} tidak bisa dikembalikan dengan pecahan yang tersedia.</p>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CashHistoryDialog({ open, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) api.get("/cash/history").then((r) => setRows(r.data)); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Riwayat Kas</DialogTitle></DialogHeader>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Belum ada riwayat</p>
        ) : (
          <div className="divide-y">
            {rows.map((r) => (
              <div key={r.id} className="py-2.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium capitalize">{r.kind}</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(r.created_at)}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.notes || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
